import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import targetIcon from "./target_icon.png";
import mapIcon from "./map_icon.png";
import measureIcon from "./measure_icon.png";
import configureIcon from "./configure_icon.png";
import watchIcon from "./watch_icon.png";
import graphIcon from "./graph_icon.png";
import handshakeIcon from "./handshake_icon.png";
import russell from "./russell_f1.png";
import paing from "./paing-khant.png";
import pratyaksh from "./pratyaksh-gupta.png";
import "./App.css";

export default function Content() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.getElementById(location.hash.replace("#", ""));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [location]);

  return (
    <div>
      {/* Hero */}
      <section id="section_home" className="banner">
        <h1>Run shared work as one operation - across every organisation involved.</h1>
        <p>
          Pairian gives clients, partners, suppliers and internal teams one
          permission-controlled way to contribute, act and move work forward.
        </p>
      </section>

      {/* Outcomes */}
      <section id="section_outcomes" className="section">
        <h2>Outcomes</h2>
        <h3 className="section-lead">
          Deliver faster. Spend less time coordinating. Create more value from every
          engagement.
        </h3>
        <p>
          Pairian helps organisations work together more efficiently, reducing delays,
          manual coordination and uncertainty throughout delivery.
        </p>

        <div className="services outcomes-grid">
          <article className="service">
            <img src={watchIcon} alt="delivery time icon" className="icon-image" />
            <h3>Shorter delivery cycles</h3>
            <p>
              Reduce the time lost waiting for information, approvals or action from
              another organisation.
            </p>
          </article>

          <article className="service">
            <img src={graphIcon} alt="increased capacity icon" className="icon-image" />
            <h3>More delivery capacity</h3>
            <p>
              Spend fewer hours chasing updates, checking what is missing,
              re-entering information and assembling outputs.
            </p>
          </article>

          <article className="service">
            <img src={handshakeIcon} alt="better experience icon" className="icon-image" />
            <h3>A better client experience</h3>
            <p>
              Give clients and partners a clearer view of progress, ownership and
              next actions, without exposing unnecessary information.
            </p>
          </article>
        </div>
      </section>

      {/* Why Pairian */}
      <section id="section_why" className="section">
        <h2>Why Pairian</h2>
        <h3 className="section-lead">
          The hardest part of delivery often sits between organisations.
        </h3>

        <p>
          Most operational systems organise work inside one business. Pairian
          organises the shared operation across clients, partners, suppliers and
          internal teams, while allowing every organisation to keep its own systems,
          responsibilities and control.
        </p>

        <div className="feature-list" aria-label="Pairian capabilities">
          <div>One live view of progress</div>
          <div>Access based on organisation and role</div>
          <div>Clear ownership and next actions</div>
          <div>Automatic progression when requirements are complete</div>
          <div>Existing specialist systems stay in place</div>
          <div>A clear record of contributions, approvals and decisions</div>
        </div>
      </section>

      {/* Live proof */}
      <section id="section_proof" className="section">
        <h2>Live Proof</h2>
        <h3 className="section-lead">Proven in a live cross-border operation.</h3>
        <p>
          Pairian currently supports an international education operation across
          the United Kingdom, Colombia and Peru.
        </p>
        <p>
          An overseas student recruitment agency, a UK programme operator,
          parents, students and boarding schools take part in the same governed
          process while keeping different responsibilities and access.
        </p>

       <div className="proof-grid">
          <article className="proof-card">
            <strong>3–4 weeks → 10 days</strong>
            <p>
              Time taken to complete the first student registration cycle.
            </p>
          </article>

          <article className="proof-card">
            <strong>2 hours → 2 seconds</strong>
            <p>
              Time to generate a batch of 11 personalised invitation letters.
            </p>
          </article>
        </div>
      </section>

      {/* How it works */}
      <section id="section_process" className="section">
        <h2>How It Works</h2>
        <h3 className="section-lead">
          Start with the outcome. Prove the value before expanding.
        </h3>
        <div className="process-steps">
          <article className="process-step">
            <img src={targetIcon} alt="Understand the result" className="smallicon-image" />
            <h3>Understand the result</h3>
            <p>
              We begin with what you want to achieve, who is involved and what
              currently slows the work down.
            </p>
          </article>
          <article className="process-step">
            <img src={mapIcon} alt="Map the real operation" className="smallicon-image" />
            <h3>Map the real operation</h3>
            <p>
              We define the organisations, roles, information, permissions,
              actions, approvals and outputs involved.
            </p>
          </article>
          <article className="process-step">
            <img src={configureIcon} alt="Configure around your environment" className="smallicon-image" />
            <h3>Configure around your environment</h3>
            <p>
              We set up the shared workflow while keeping organisational
              boundaries and existing specialist systems in place.
            </p>
          </article>
          <article className="process-step">
            <img src={measureIcon} alt="Measure and expand" className="smallicon-image" />
            <h3>Measure and expand</h3>
            <p>
              We start with one real workflow, measure the improvement and expand
              only where the value is clear.
            </p>
          </article>
        </div>
      </section>

      {/* About */}
      <section id="section_about" className="section contact-section">
        <h2>About</h2>
        <h3 className="section-lead">
          Built by a team focused on making complex shared work easier to run.
        </h3>

        <div className="team-grid">
          <article className="team-card">
            <img src={paing} alt="Founder - Paing" className="founder-photo" />
            <h3>Paing Khant</h3>
            <p className="team-role">Founder &amp; CEO</p>
            <p>
              Leads Pairian's company strategy, partnerships and product direction.
              Focused on building the infrastructure that helps organisations work
              together more effectively.
            </p>
          </article>
          <article className="team-card">
            <img src={russell} alt="Cofounder - Russell" className="founder-photo" />
            <h3>Russell Charman</h3>
            <p className="team-role">Co-founder &amp; CTO</p>
            <p>
              Leads Pairian's product architecture and engineering. Brings
              extensive experience delivering software in demanding environments,
              including McLaren Racing and PayPoint.
            </p>
          </article>
          <article className="team-card">
            <img src={pratyaksh} alt="engineer - Pratyaksh" className="founder-photo" />
            <h3>Pratyaksh Gupta</h3>
            <p className="team-role">Research &amp; Productisation Engineer</p>
            <p>
              Leads workflow research and productisation, turning complex
              operations into clear, repeatable applications of Pairian.
            </p>
          </article>
        </div>
      </section>

    </div>
  );
}